import { useEffect, useState } from "react";
import { api, errMsg } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent } from "@/components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger } from "@/components/ui/dialog";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import { Plus, Trash2, Fuel, Flame, Printer } from "lucide-react";
import { toast } from "sonner";
import { printTable, num } from "@/lib/printReport";

const fmt = (n) => "₹" + Number(n || 0).toLocaleString("en-IN");
const L = (n) => Number(n || 0).toLocaleString("en-IN") + " L";
const today = () => new Date().toISOString().slice(0, 10);

function Stat({ label, value, sub, accent, testid }) {
  return (
    <Card className="bg-card border-border">
      <CardContent className="p-4">
        <p className="text-xs uppercase text-muted-foreground font-mono-sku">{label}</p>
        <p className={`text-2xl font-extrabold ${accent}`} data-testid={testid}>{value}</p>
        {sub && <p className="text-xs text-muted-foreground mt-1">{sub}</p>}
      </CardContent>
    </Card>
  );
}

export default function Diesel() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [stock, setStock] = useState(null);
  const [inward, setInward] = useState([]);
  const [consumption, setConsumption] = useState([]);
  const [openIn, setOpenIn] = useState(false);
  const [openOut, setOpenOut] = useState(false);
  const emptyIn = { date: today(), supplier: "", quantity_l: "", rate_per_l: "", invoice_no: "", notes: "" };
  const emptyOut = { date: today(), equipment: "", quantity_l: "", operator: "", purpose: "Generator", shift: "", notes: "" };
  const [fin, setFin] = useState(emptyIn);
  const [fout, setFout] = useState(emptyOut);
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const inRange = (r) => (!from || r.date >= from) && (!to || r.date <= to);
  const inwardShown = inward.filter(inRange);
  const consShown = consumption.filter(inRange);
  const rangeLabel = `Period: ${from || "Start"} to ${to || "Today"}`;

  const printInward = () => printTable({
    title: "Diesel Inward Report", subtitle: rangeLabel, rows: inwardShown,
    columns: [{ key: "date", label: "Date" }, { key: "supplier", label: "Supplier" }, { key: "invoice_no", label: "Invoice" },
      { label: "Qty (L)", right: true, render: (r) => num(r.quantity_l) }, { label: "Rate (₹/L)", right: true, render: (r) => num(r.rate_per_l) },
      { key: "notes", label: "Notes" }, { key: "created_by", label: "By" }, { label: "Cost (₹)", right: true, render: (r) => num(r.total_cost) }],
    totals: [{ label: `Total Quantity: ${num(inwardShown.reduce((s, r) => s + r.quantity_l, 0))} L — Total Cost`, value: num(inwardShown.reduce((s, r) => s + (r.total_cost || 0), 0)) },
      ...(stock ? [{ label: "Current Tank Stock (L)", value: num(stock.current_stock_l) }] : [])],
  });
  const printConsumption = () => printTable({
    title: "Diesel Consumption Report", subtitle: rangeLabel, rows: consShown,
    columns: [{ key: "date", label: "Date" }, { key: "equipment", label: "Equipment / DG" }, { key: "operator", label: "Operator" },
      { key: "purpose", label: "Purpose" }, { key: "shift", label: "Shift" }, { key: "notes", label: "Notes" }, { key: "created_by", label: "By" },
      { label: "Qty (L)", right: true, render: (r) => num(r.quantity_l) }],
    totals: [{ label: "Total Consumed (L)", value: num(consShown.reduce((s, r) => s + r.quantity_l, 0)) },
      ...(stock ? [{ label: "Current Tank Stock (L)", value: num(stock.current_stock_l) }] : [])],
  });

  const load = () => {
    Promise.all([api.get("/fuel/summary"), api.get("/fuel/inward"), api.get("/fuel/consumption")])
      .then(([s, i, c]) => { setStock(s.data.stock); setInward(i.data); setConsumption(c.data); })
      .catch((e) => toast.error(errMsg(e)));
  };
  useEffect(() => { load(); }, []);

  const submitIn = async (e) => {
    e.preventDefault();
    try {
      await api.post("/fuel/inward", { ...fin, quantity_l: parseFloat(fin.quantity_l), rate_per_l: parseFloat(fin.rate_per_l || 0) });
      toast.success("Diesel inward recorded"); setOpenIn(false); setFin(emptyIn); load();
    } catch (err) { toast.error(errMsg(err)); }
  };
  const submitOut = async (e) => {
    e.preventDefault();
    try {
      await api.post("/fuel/consumption", { ...fout, quantity_l: parseFloat(fout.quantity_l) });
      toast.success("Consumption recorded"); setOpenOut(false); setFout(emptyOut); load();
    } catch (err) { toast.error(errMsg(err)); }
  };
  const del = async (kind, id) => {
    try { await api.delete(`/fuel/${kind}/${id}`); toast.success("Deleted"); load(); } catch (err) { toast.error(errMsg(err)); }
  };

  const field = (f, set, key, label, props = {}) => (
    <div className="space-y-2">
      <Label>{label}</Label>
      <Input data-testid={`diesel-${key}-input`} value={f[key]} onChange={(e) => set({ ...f, [key]: e.target.value })} {...props} />
    </div>
  );

  return (
    <div className="space-y-6 animate-fade-up" data-testid="diesel-page">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <h1 className="text-3xl sm:text-4xl font-extrabold tracking-tight">Diesel Ledger</h1>
          <p className="text-muted-foreground mt-1">Fuel inward, consumption and live tank balance.</p>
        </div>
        <div className="flex gap-2">
          <Dialog open={openIn} onOpenChange={setOpenIn}>
            <DialogTrigger asChild>
              <Button data-testid="add-diesel-inward-btn" className="bg-emerald-500 hover:bg-emerald-600 text-white gap-2"><Plus className="h-4 w-4" /> Diesel Inward</Button>
            </DialogTrigger>
            <DialogContent className="bg-card border-border">
              <DialogHeader><DialogTitle>Record Diesel Inward</DialogTitle></DialogHeader>
              <form onSubmit={submitIn} className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  {field(fin, setFin, "date", "Date", { type: "date", required: true })}
                  {field(fin, setFin, "supplier", "Supplier", { required: true, placeholder: "RC Filling Station" })}
                </div>
                <div className="grid grid-cols-2 gap-4">
                  {field(fin, setFin, "quantity_l", "Quantity (L)", { type: "number", step: "0.01", min: "0", required: true })}
                  {field(fin, setFin, "rate_per_l", "Rate / L (₹)", { type: "number", step: "0.01", min: "0", placeholder: "Default from settings" })}
                </div>
                {field(fin, setFin, "invoice_no", "Invoice No.", { placeholder: "Optional" })}
                {field(fin, setFin, "notes", "Notes", { placeholder: "Optional" })}
                <DialogFooter><Button type="submit" data-testid="diesel-inward-submit-btn" className="bg-emerald-500 hover:bg-emerald-600 text-white">Save</Button></DialogFooter>
              </form>
            </DialogContent>
          </Dialog>
          <Dialog open={openOut} onOpenChange={setOpenOut}>
            <DialogTrigger asChild>
              <Button data-testid="add-diesel-consumption-btn" className="bg-amber-500 hover:bg-amber-600 text-white gap-2"><Flame className="h-4 w-4" /> Consumption</Button>
            </DialogTrigger>
            <DialogContent className="bg-card border-border">
              <DialogHeader><DialogTitle>Record Diesel Consumption</DialogTitle></DialogHeader>
              <form onSubmit={submitOut} className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  {field(fout, setFout, "date", "Date", { type: "date", required: true })}
                  {field(fout, setFout, "equipment", "Equipment / DG", { required: true, placeholder: "DG 380 KVA" })}
                </div>
                <div className="grid grid-cols-2 gap-4">
                  {field(fout, setFout, "quantity_l", "Quantity (L)", { type: "number", step: "0.01", min: "0", required: true })}
                  {field(fout, setFout, "operator", "Operator", { placeholder: "Name" })}
                </div>
                <div className="grid grid-cols-2 gap-4">
                  {field(fout, setFout, "purpose", "Purpose", { placeholder: "Generator" })}
                  {field(fout, setFout, "shift", "Shift", { placeholder: "Day / Night" })}
                </div>
                {field(fout, setFout, "notes", "Notes", { placeholder: "Optional" })}
                <DialogFooter><Button type="submit" data-testid="diesel-consumption-submit-btn" className="bg-amber-500 hover:bg-amber-600 text-white">Save</Button></DialogFooter>
              </form>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      {stock && (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          <Stat label="Current Stock" value={L(stock.current_stock_l)} accent={stock.is_low_stock ? "text-rose-400" : "text-emerald-400"} testid="diesel-current-stock"
            sub={stock.is_low_stock ? `Low! Threshold ${L(stock.low_stock_threshold_l)}` : `Threshold ${L(stock.low_stock_threshold_l)}`} />
          <Stat label="Total Inward" value={L(stock.total_inward_l)} accent="text-sky-400" testid="diesel-total-inward" sub={`Opening ${L(stock.opening_stock_l)}`} />
          <Stat label="Total Consumed" value={L(stock.total_consumed_l)} accent="text-amber-400" testid="diesel-total-consumed" />
          <Stat label="Total Fuel Cost" value={fmt(stock.total_inward_cost)} accent="text-rose-400" testid="diesel-total-cost" />
        </div>
      )}

      <Tabs defaultValue="inward">
        <div className="flex flex-wrap items-end gap-4">
          <TabsList className="bg-card border border-border">
            <TabsTrigger value="inward" data-testid="diesel-tab-inward">Inward ({inwardShown.length})</TabsTrigger>
            <TabsTrigger value="consumption" data-testid="diesel-tab-consumption">Consumption ({consShown.length})</TabsTrigger>
          </TabsList>
          <div className="space-y-1"><Label className="text-xs">From</Label>
            <Input type="date" className="w-40" value={from} onChange={(e) => setFrom(e.target.value)} data-testid="diesel-filter-from" /></div>
          <div className="space-y-1"><Label className="text-xs">To</Label>
            <Input type="date" className="w-40" value={to} onChange={(e) => setTo(e.target.value)} data-testid="diesel-filter-to" /></div>
          <div className="ml-auto flex gap-2">
            <Button variant="outline" data-testid="print-diesel-inward-btn" onClick={printInward} className="gap-2 border-border"><Printer className="h-4 w-4" /> Diesel Report</Button>
            <Button variant="outline" data-testid="print-diesel-consumption-btn" onClick={printConsumption} className="gap-2 border-border"><Printer className="h-4 w-4" /> Consumption Report</Button>
          </div>
        </div>
        <TabsContent value="inward">
          <Card className="bg-card border-border"><CardContent className="p-0 overflow-x-auto">
            <table className="w-full text-sm">
              <thead><tr className="border-b border-border text-left text-muted-foreground">
                <th className="px-4 py-3 font-medium">Date</th><th className="px-4 py-3 font-medium">Supplier</th>
                <th className="px-4 py-3 font-medium text-right">Qty</th><th className="px-4 py-3 font-medium text-right">Rate</th>
                <th className="px-4 py-3 font-medium text-right">Cost</th><th className="px-4 py-3 font-medium">Invoice</th>
                <th className="px-4 py-3 font-medium">By</th><th className="px-4 py-3"></th>
              </tr></thead>
              <tbody data-testid="diesel-inward-table-body">
                {inwardShown.length === 0 ? (
                  <tr><td colSpan={8} className="px-4 py-12 text-center text-muted-foreground"><Fuel className="h-10 w-10 mx-auto mb-2 opacity-40" />No diesel inward yet.</td></tr>
                ) : inwardShown.map((r) => (
                  <tr key={r.id} className="border-b border-border/50 hover:bg-white/5">
                    <td className="px-4 py-3 font-mono-sku text-xs">{r.date}</td>
                    <td className="px-4 py-3 font-medium">{r.supplier}</td>
                    <td className="px-4 py-3 text-right font-mono-sku text-emerald-400 font-semibold">+{r.quantity_l} L</td>
                    <td className="px-4 py-3 text-right font-mono-sku">{fmt(r.rate_per_l)}</td>
                    <td className="px-4 py-3 text-right font-mono-sku">{fmt(r.total_cost)}</td>
                    <td className="px-4 py-3 text-muted-foreground">{r.invoice_no || "—"}</td>
                    <td className="px-4 py-3 text-muted-foreground">{r.created_by}</td>
                    <td className="px-4 py-3 text-right">{isAdmin && <Button size="icon" variant="ghost" data-testid={`diesel-inward-delete-${r.id}`} onClick={() => del("inward", r.id)} className="h-8 w-8 text-rose-400 hover:bg-rose-500/10"><Trash2 className="h-4 w-4" /></Button>}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </CardContent></Card>
        </TabsContent>
        <TabsContent value="consumption">
          <Card className="bg-card border-border"><CardContent className="p-0 overflow-x-auto">
            <table className="w-full text-sm">
              <thead><tr className="border-b border-border text-left text-muted-foreground">
                <th className="px-4 py-3 font-medium">Date</th><th className="px-4 py-3 font-medium">Equipment</th>
                <th className="px-4 py-3 font-medium text-right">Qty</th><th className="px-4 py-3 font-medium">Operator</th>
                <th className="px-4 py-3 font-medium">Purpose</th><th className="px-4 py-3 font-medium">Shift</th>
                <th className="px-4 py-3 font-medium">By</th><th className="px-4 py-3"></th>
              </tr></thead>
              <tbody data-testid="diesel-consumption-table-body">
                {consShown.length === 0 ? (
                  <tr><td colSpan={8} className="px-4 py-12 text-center text-muted-foreground"><Flame className="h-10 w-10 mx-auto mb-2 opacity-40" />No consumption entries yet.</td></tr>
                ) : consShown.map((r) => (
                  <tr key={r.id} className="border-b border-border/50 hover:bg-white/5">
                    <td className="px-4 py-3 font-mono-sku text-xs">{r.date}</td>
                    <td className="px-4 py-3 font-medium">{r.equipment}</td>
                    <td className="px-4 py-3 text-right font-mono-sku text-amber-400 font-semibold">-{r.quantity_l} L</td>
                    <td className="px-4 py-3">{r.operator || "—"}</td>
                    <td className="px-4 py-3"><Badge variant="outline" className="text-sky-300 border-sky-500/30">{r.purpose || "—"}</Badge></td>
                    <td className="px-4 py-3 text-muted-foreground">{r.shift || "—"}</td>
                    <td className="px-4 py-3 text-muted-foreground">{r.created_by}</td>
                    <td className="px-4 py-3 text-right">{isAdmin && <Button size="icon" variant="ghost" data-testid={`diesel-consumption-delete-${r.id}`} onClick={() => del("consumption", r.id)} className="h-8 w-8 text-rose-400 hover:bg-rose-500/10"><Trash2 className="h-4 w-4" /></Button>}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </CardContent></Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
